import {
	createContext,
	useEffect,
	useRef,
	useState,
	type ReactNode,
} from 'react'
import { NAV_EVENT } from '@router'
import { OfflineBanner } from './OfflineBanner.tsx'
import { TabBar } from './TabBar.tsx'
import styles from './AppShell.module.css'

// The mini-app's FAB portals into this slot, which lives in the
// non-scrolling shell layer (a sibling of the scroll container). That
// keeps the FAB glued above the tab bar without the iOS overscroll
// glitch a viewport-`fixed` element suffers. Null until the slot mounts.
export const FabSlotContext = createContext<HTMLElement | null>(null)

// App shell: a fixed-height column whose content scrolls on its own, with an
// offline banner above it, the tab bar pinned below it and a FAB slot anchored
// bottom-right.
export const AppShell = ({ children }: { children: ReactNode }) => {
	const [fabSlot, setFabSlot] = useState<HTMLDivElement | null>(null)
	const scrollRef = useRef<HTMLDivElement>(null)

	// The scroll container persists across view swaps, so its scroll position
	// carries over. Reset it to the top on each forward navigation (Link /
	// navigate) — browser back/forward fire popstate, not this event, so they
	// keep their position.
	useEffect(() => {
		const onNav = () => scrollRef.current?.scrollTo(0, 0)
		window.addEventListener(NAV_EVENT, onNav)
		return () => window.removeEventListener(NAV_EVENT, onNav)
	}, [])

	return (
		<FabSlotContext.Provider value={fabSlot}>
			<div className={styles.shell}>
				<OfflineBanner />
				<div ref={scrollRef} className={styles.scroll}>
					{children}
				</div>
				<div ref={setFabSlot} className={styles.fabSlot} />
				<TabBar />
			</div>
		</FabSlotContext.Provider>
	)
}
